import { createZip } from 'littlezipper';

import { DeliveryNoteByIdsAdminAsset } from '~/data/delivery/useQueryDeliveryNotesByIdsAdmin';

export type DeliveryNoteExportFile = {
  blob: Blob;
  deliveryNoteId: string;
  deliveryNoteNumber?: string;
  extension: 'json' | 'pdf';
};

const INVALID_FILENAME_CHARACTER_REGEX = /[\\/:*?"<>|]/g;

const sanitizeFileName = (value: string): string =>
  value
    .replace(INVALID_FILENAME_CHARACTER_REGEX, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 180);

const createUniquePath = ({
  extension,
  fileNameCandidate,
  usedPaths,
}: {
  extension: DeliveryNoteExportFile['extension'];
  fileNameCandidate: string;
  usedPaths: Set<string>;
}) => {
  const sanitizedBaseName =
    sanitizeFileName(fileNameCandidate) || 'delivery-note';
  let uniquePath = `${sanitizedBaseName}.${extension}`;
  let suffix = 2;

  while (usedPaths.has(uniquePath)) {
    uniquePath = `${sanitizedBaseName}-${suffix}.${extension}`;
    suffix += 1;
  }

  usedPaths.add(uniquePath);
  return uniquePath;
};

export const getDeliveryNoteNumberFromAsset = (
  asset: DeliveryNoteByIdsAdminAsset,
): string | undefined => {
  const trimmedId = asset.assetState?.body?.header?.id?.trim();
  return trimmedId ? trimmedId : undefined;
};

export const createDeliveryNotesZipBlob = async ({
  files,
  onProgress,
}: {
  files: DeliveryNoteExportFile[];
  onProgress?: (current: number, total: number) => void;
}): Promise<Blob> => {
  const zipFiles: Array<{ data: Uint8Array; path: string }> = [];
  const usedPaths = new Set<string>();

  for (const [index, file] of files.entries()) {
    const path = createUniquePath({
      extension: file.extension,
      fileNameCandidate: file.deliveryNoteNumber ?? file.deliveryNoteId,
      usedPaths,
    });

    const fileBuffer = await file.blob.arrayBuffer();

    zipFiles.push({
      data: new Uint8Array(fileBuffer),
      path,
    });

    onProgress?.(index + 1, files.length);
  }

  const zipData = await createZip(zipFiles);
  const zipBytes = new Uint8Array(zipData.byteLength);
  zipBytes.set(zipData);

  return new Blob([zipBytes], { type: 'application/zip' });
};

export const mapAssetsToJsonExportFiles = ({
  assets,
}: {
  assets: DeliveryNoteByIdsAdminAsset[];
}): DeliveryNoteExportFile[] =>
  assets
    .map((asset) => {
      return {
        blob: new Blob([JSON.stringify(asset, null, 2)], {
          type: 'application/json;charset=utf-8',
        }),
        deliveryNoteId: asset.id,
        deliveryNoteNumber: getDeliveryNoteNumberFromAsset(asset),
        extension: 'json' as const,
      };
    })
    .filter(Boolean);
